import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';
import { setActiveLanguage } from '../utils/routes';
import {
  Shield, Crosshair, ChevronRight, Settings, Crown,
  Radio, Activity, Target, Globe, Hammer, Shirt, Trees, Lock, Gavel
} from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Statik Yapı (Resimler API'den gelecek, burası sadece iskelet)
const TRADERS_CONFIG = [
  { id: 'prapor', nameKey: 'trader_prapor', maxLL: 4, icon: Radio },
  { id: 'therapist', nameKey: 'trader_therapist', maxLL: 4, icon: Activity },
  { id: 'skier', nameKey: 'trader_skier', maxLL: 4, icon: Target },
  { id: 'peacekeeper', nameKey: 'trader_peacekeeper', maxLL: 4, icon: Globe },
  { id: 'mechanic', nameKey: 'trader_mechanic', maxLL: 4, icon: Hammer },
  { id: 'ragman', nameKey: 'trader_ragman', maxLL: 4, icon: Shirt },
  { id: 'jaeger', nameKey: 'trader_jaeger', maxLL: 4, icon: Trees },
  { id: 'fence', nameKey: 'trader_fence', maxLL: 2, icon: Lock },
  { id: 'ref', nameKey: 'trader_ref', maxLL: 4, icon: Gavel }
];

const TRADER_REQUIREMENTS = {
  prapor: { 1: { level: 1, rep: 0 }, 2: { level: 15, rep: 0.20 }, 3: { level: 28, rep: 0.35 }, 4: { level: 37, rep: 0.60 } },
  therapist: { 1: { level: 1, rep: 0 }, 2: { level: 12, rep: 0.20 }, 3: { level: 22, rep: 0.40 }, 4: { level: 34, rep: 0.60 } },
  skier: { 1: { level: 1, rep: 0 }, 2: { level: 15, rep: 0.20 }, 3: { level: 28, rep: 0.35 }, 4: { level: 38, rep: 0.60 } },
  peacekeeper: { 1: { level: 1, rep: 0 }, 2: { level: 14, rep: 0.20 }, 3: { level: 23, rep: 0.40 }, 4: { level: 37, rep: 0.75 } },
  mechanic: { 1: { level: 1, rep: 0 }, 2: { level: 15, rep: 0.20 }, 3: { level: 30, rep: 0.40 }, 4: { level: 40, rep: 0.60 } },
  ragman: { 1: { level: 1, rep: 0 }, 2: { level: 17, rep: 0.20 }, 3: { level: 32, rep: 0.40 }, 4: { level: 42, rep: 0.60 } },
  jaeger: { 1: { level: 1, rep: 0 }, 2: { level: 15, rep: 0.20 }, 3: { level: 26, rep: 0.40 }, 4: { level: 35, rep: 0.60 } },
  fence: { 1: { level: 1, rep: -1.0 }, 2: { level: 1, rep: 6.0 } },
  ref: { 1: { level: 1, rep: 0 }, 2: { level: 20, rep: 0.20 }, 3: { level: 35, rep: 0.40 }, 4: { level: 45, rep: 0.60 } }
};

function Welcome({ onComplete }) {
  const { t, changeLanguage, language } = useLanguage();

  const { traders: apiTraders = [], hideout: hideoutData = [] } = useData();

  const [gameEdition, setGameEdition] = useState('standard');
  const [faction, setFaction] = useState('USEC');
  const [level, setLevel] = useState(1);
  const [fenceRep, setFenceRep] = useState(0);
  const [traderLevels, setTraderLevels] = useState(TRADERS_CONFIG.reduce((acc, trader) => ({ ...acc, [trader.id]: 1 }), {}));

  const editionKeys = {
    standard: 'edition_standard',
    leftBehind: 'edition_left_behind',
    prepareForEscape: 'edition_prepare',
    edgeOfDarkness: 'edition_eod',
    unheard: 'edition_unheard'
  };

  // LEVEL & REP MANTIĞI
  useEffect(() => {
    setTraderLevels(prevLevels => {
      const newLevels = {};
      const safeLevel = parseInt(level) || 1;
      const safeFenceRep = parseFloat(fenceRep) || 0;

      TRADERS_CONFIG.forEach(trader => {
        let maxAvailable = 1;
        for (let ll = 1; ll <= trader.maxLL; ll++) {
          const req = TRADER_REQUIREMENTS[trader.id][ll];
          if (!req) continue;
          if (trader.id === 'fence') {
            if (safeFenceRep >= req.rep) maxAvailable = ll;
          } else {
            if (safeLevel >= req.level) maxAvailable = ll;
          }
        }
        newLevels[trader.id] = Math.min(prevLevels[trader.id] || 1, maxAvailable);
      });
      return newLevels;
    });
  }, [level, fenceRep]);

  const canSelectTraderLevel = (traderId, targetLevel) => {
    const req = TRADER_REQUIREMENTS[traderId][targetLevel];
    if (!req) return false;
    const safeLevel = parseInt(level) || 1;
    const safeFenceRep = parseFloat(fenceRep) || 0;
    if (traderId === 'fence') return safeFenceRep >= req.rep;
    return safeLevel >= req.level;
  };

  const handleTraderLevelClick = (traderId, targetLevel) => {
    if (canSelectTraderLevel(traderId, targetLevel)) {
      setTraderLevels(prev => ({ ...prev, [traderId]: targetLevel }));
    }
  };

  const handleLevelBlur = () => {
    let val = parseInt(level);
    if (isNaN(val) || val < 1) val = 1;
    if (val > 79) val = 79;
    setLevel(val);
  };

  const handleFenceRepBlur = () => {
    let val = parseFloat(fenceRep);
    if (isNaN(val)) val = 0;
    if (val < -20) val = -20;
    if (val > 20) val = 20;
    setFenceRep(parseFloat(val.toFixed(2)));
  };

  const handleInitialize = () => {
    const initialHideout = {};
    let targetStashLevel = 1;
    if (gameEdition === 'unheard' || gameEdition === 'edgeOfDarkness') targetStashLevel = 4;
    else if (gameEdition === 'prepareForEscape') targetStashLevel = 3;
    else if (gameEdition === 'leftBehind') targetStashLevel = 2;

    const stashModule = hideoutData.find(m => m.name === 'Stash');
    const stashId = stashModule ? stashModule.id : '5d484fc0654e76006657e0ab';
    initialHideout[stashId] = targetStashLevel;

    if (gameEdition === 'unheard') {
      const cultistModule = hideoutData.find(m => m.name === 'Cultist Circle');
      const cultistId = cultistModule ? cultistModule.id : '667298e75ea6b4493c08f266';
      if (cultistId) initialHideout[cultistId] = 1;
    }

    const profile = {
      gameEdition,
      faction,
      level: parseInt(level),
      fenceRep: parseFloat(fenceRep),
      traderLevels,
      completedQuests: [],
      completedObjectives: {},
      hideoutModules: initialHideout,
      settings: { theme: 'dark', background: 'bg-gradient-to-br from-[#0B0E14] to-[#1a1f2e]', language: language },
      createdAt: new Date().toISOString()
    };

    localStorage.setItem('tarkovProfile', JSON.stringify(profile));
    onComplete(profile);
  };

  return (
    <div className="bg-[#0f1216]/95 backdrop-blur-xl border border-emerald-500/20 rounded-[2rem] p-10 w-full max-w-6xl shadow-[0_0_80px_rgba(16,185,129,0.1)] relative overflow-hidden animate-fadeIn flex flex-col gap-8 ring-1 ring-emerald-900/50">

      {/* DİL SEÇİCİ */}
      <div className="absolute top-8 right-8 flex gap-2 z-20">
        {['en', 'tr', 'ru'].map(lang => (
          <button
            key={lang}
            onClick={() => {
              setActiveLanguage(lang);
              changeLanguage(lang);
            }}
            className={`text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all border ${language === lang ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-[#1a1d24] text-gray-400 border-gray-700 hover:text-white hover:border-emerald-500/50'}`}
          >
            {lang.toUpperCase()}
          </button>
        ))}
      </div>

      {/* BAŞLIK VE AÇIKLAMA */}
      <div className="text-center relative">
        <div className="inline-flex p-3 rounded-2xl bg-emerald-600/10 border border-emerald-500/20 shadow-lg shadow-emerald-500/10 mb-4">
          <Settings className="w-8 h-8 text-emerald-500" />
        </div>
        <h1 className="text-3xl font-black text-white tracking-tight mb-2">{t('welcome_title')}</h1>
        <p className="text-gray-300 text-sm font-medium mb-1">{t('welcome_subtitle')}</p>
        <p className="text-gray-500 text-xs max-w-lg mx-auto">{t('welcome_desc')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

        {/* SOL KOLON: Ayarlar (4/12) */}
        <div className="lg:col-span-4 flex flex-col gap-6">

          {/* Game Edition */}
          <div className="group">
            <label className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2 block pl-1 group-hover:text-emerald-400 transition-colors">{t('game_edition')}</label>
            <div className="relative">
              <select
                value={gameEdition}
                onChange={(e) => setGameEdition(e.target.value)}
                className="w-full bg-[#1a1d24] border border-gray-700 text-white rounded-xl px-4 py-3.5 text-sm font-bold appearance-none focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all cursor-pointer hover:bg-[#252932]"
              >
                {Object.entries(editionKeys).map(([key, transKey]) => (
                  <option key={key} value={key}>{t(transKey)}</option>
                ))}
              </select>
              <ChevronRight className="w-4 h-4 text-gray-500 absolute right-4 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
            </div>
          </div>

          {/* Faction */}
          <div className="group">
            <label className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2 block pl-1 group-hover:text-emerald-400 transition-colors">{t('faction')}</label>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setFaction('USEC')} className={`py-3.5 rounded-xl font-bold text-sm transition-all border flex items-center justify-center gap-2 ${faction === 'USEC' ? 'bg-blue-600/10 border-blue-500 text-blue-400 shadow-lg shadow-blue-900/20' : 'bg-[#1a1d24] border-gray-700 text-gray-500 hover:border-emerald-500/50 hover:text-white'}`}>
                <Shield className="w-4 h-4" /> USEC
              </button>
              <button onClick={() => setFaction('BEAR')} className={`py-3.5 rounded-xl font-bold text-sm transition-all border flex items-center justify-center gap-2 ${faction === 'BEAR' ? 'bg-red-600/10 border-red-500 text-red-400 shadow-lg shadow-red-900/20' : 'bg-[#1a1d24] border-gray-700 text-gray-500 hover:border-emerald-500/50 hover:text-white'}`}>
                <Crosshair className="w-4 h-4" /> BEAR
              </button>
            </div>
          </div>

          {/* Level & Rep */}
          <div className="grid grid-cols-2 gap-4">
            <div className="group">
              <label className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2 block pl-1 group-hover:text-emerald-400 transition-colors">{t('level')}</label>
              <input type="number" value={level} onChange={(e) => setLevel(e.target.value)} onBlur={handleLevelBlur} className="w-full bg-[#1a1d24] border border-gray-700 text-white rounded-xl px-4 py-3.5 text-lg font-black focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-center hover:bg-[#252932]" />
            </div>
            <div className="group">
              {/* RUSÇA FIX: Uzun başlıklar için truncate ve title eklendi */}
              <label className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2 block pl-1 group-hover:text-emerald-400 transition-colors truncate" title={t('fence_rep')}>{t('fence_rep')}</label>
              <input type="number" value={fenceRep} onChange={(e) => setFenceRep(e.target.value)} onBlur={handleFenceRepBlur} step="0.01" className="w-full bg-[#1a1d24] border border-gray-700 text-white rounded-xl px-4 py-3.5 text-lg font-black focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-center hover:bg-[#252932]" />
            </div>
          </div>
        </div>

        {/* SAĞ KOLON: Traderlar (8/12) */}
        <div className="lg:col-span-8 flex flex-col">
          <label className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-4 pl-1">{t('trader_levels')}</label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 h-full">
            {TRADERS_CONFIG.map(config => {
              // API'den bu trader'ın verisini bul (Resim için)
              const apiData = apiTraders?.find(t => t.normalizedName === config.id);
              // İkon (Fallback)
              const Icon = config.icon;
              // İsim Çevirisi
              const traderName = t(config.nameKey) || config.id.charAt(0).toUpperCase() + config.id.slice(1);

              return (
                <div key={config.id} className="bg-[#1a1d24] border border-gray-700/50 rounded-xl p-3 flex items-center justify-between hover:bg-[#20242c] hover:border-emerald-500/30 transition-all group">

                  {/* Trader Info & Photo (API) */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#111318] border border-white/5 flex items-center justify-center text-gray-500 overflow-hidden group-hover:border-emerald-500/50 transition-all">
                      {apiData?.imageLink ? (
                        <LazyLoadImage src={apiData.imageLink} alt={traderName} effect="blur" className="w-full h-full object-cover" />
                      ) : (
                        <Icon className="w-5 h-5 group-hover:text-emerald-400 transition-colors" />
                      )}
                    </div>
                    <span className="text-gray-300 font-bold text-sm truncate group-hover:text-white transition-colors">{traderName}</span>
                  </div>

                  {/* Levels & Crown Logic */}
                  <div className="flex gap-1">
                    {[...Array(config.maxLL)].map((_, idx) => {
                      const lvl = idx + 1;
                      const isAvailable = canSelectTraderLevel(config.id, lvl);
                      const isSelected = traderLevels[config.id] >= lvl;
                      const isMax = lvl === config.maxLL; // Son seviye mi? (Taç kontrolü)

                      // Kilitliyse Tooltip Metni
                      let titleText = "";
                      if (!isAvailable) {
                        const req = TRADER_REQUIREMENTS[config.id][lvl];
                        titleText = config.id === 'fence'
                          ? `Requires Rep: ${req.rep}`
                          : `Requires Level: ${req.level}`;
                      }

                      return (
                        <button
                          key={lvl}
                          title={titleText}
                          onClick={() => handleTraderLevelClick(config.id, lvl)}
                          disabled={!isAvailable}
                          className={`
                            h-8 w-8 rounded-md flex items-center justify-center text-xs font-black transition-all border
                            ${isSelected
                              ? isMax
                                ? 'bg-yellow-600/20 border-yellow-500 text-yellow-400 shadow-[0_0_10px_rgba(234,179,8,0.2)]' // ALTIN TAÇ
                                : 'bg-emerald-600/20 border-emerald-500 text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.2)]' // YEŞİL SEVİYE
                              : isAvailable
                                ? 'bg-[#2a2e37] border-gray-600 text-gray-400 hover:bg-[#323741] hover:text-white hover:border-emerald-500/50'
                                : 'bg-[#111318] border-transparent text-gray-700 cursor-not-allowed'}
                          `}
                        >
                          {/* Sadece seçiliyse ve Max level ise Taç göster */}
                          {isSelected && isMax ? <Crown className="w-4 h-4" /> : lvl}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <button onClick={handleInitialize} className="w-full mt-auto bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white py-4 rounded-xl font-black text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2 shadow-xl shadow-emerald-900/20 hover:scale-[1.02] active:scale-[0.98]">
        {t('initialize_btn')} <ChevronRight className="w-5 h-5" strokeWidth={3} />
      </button>
    </div>
  );
}

export default Welcome;
